import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Stack,
  Button,
  Box,
  Alert,
  Typography,
  MenuItem,
  Avatar,
  InputAdornment,
  Divider,
  Chip,
  Card,
  Collapse,
} from "@mui/material";
import {
  Add as AddIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon,
  Close as CloseIcon,
  AddCircle as AddCircleIcon,
  Language as LanguageIcon,
  Person as PersonIcon,
  CreditCard as CreditCardIcon,
  CalendarToday as CalendarTodayIcon,
  Category as CategoryIcon,
  Stars as StarsIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { createVersionToUser } from "../../../Services/BrokersApi";
import { getPlane } from "../../../Services/plansApi";
import { themeColors } from "../../../theme/colors";

export default function CreateVersionDialog({ open, userId, onClose, onCreated }) {
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    domain: "",
    user_id: "",
    plane_id: "",
    cost: "",
    start_date: "",
    end_date: "",
    is_demo: "0",
    // كل خصائص الميزة المدعومة من الAPI
    features: [], // each: { feature_id, value, yearly_cost, monthly_cost, start_date, end_date }
  });
  // قائمة الباقات لجلب الأسماء وعرضها في select
  const [planes, setPlanes] = useState([]);
  const [featuresExpanded, setFeaturesExpanded] = useState(false);

  // جلب الباقات عند فتح الديالوج
  useEffect(() => {
    if (!open) return;
    const fetchPlanes = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const res = await getPlane(null, token);
        console.log("getPlane full response:", res);
        console.log("res.data.data:", res?.data?.data);
        console.log("res.data.data.palnes:", res?.data?.data?.palnes);
        
        // استخراج المصفوفة من الاستجابة (تدعم data.palnes، data.planes، أو مباشرة)
        let list = [];
        // الأولوية: data.data.palnes (المستوى الحقيقي حسب console)
        if (res?.data?.data?.palnes && Array.isArray(res.data.data.palnes)) {
          list = res.data.data.palnes;
        } else if (res?.data?.data?.planes && Array.isArray(res.data.data.planes)) {
          list = res.data.data.planes;
        } else if (res?.data?.palnes && Array.isArray(res.data.palnes)) {
          list = res.data.palnes;
        } else if (res?.data?.planes && Array.isArray(res.data.planes)) {
          list = res.data.planes;
        } else if (res?.data?.data && Array.isArray(res.data.data)) {
          // بعض الاستجابات قد تحتوي data.data
          list = res.data.data;
        } else if (res?.palnes && Array.isArray(res.palnes)) {
          list = res.palnes;
        } else if (res?.planes && Array.isArray(res.planes)) {
          list = res.planes;
        } else if (res?.data && Array.isArray(res.data)) {
          // أحياناً data نفسها مصفوفة
          list = res.data;
        } else if (Array.isArray(res)) {
          list = res;
        }
        console.log("Extracted planes list:", list); // للتأكد من الاستخراج
        console.log("Planes count:", list.length);
        if (list.length > 0) console.log("First plane:", list[0]);
        
        setPlanes(list);
      } catch (e) {
        console.error("Error fetching planes:", e);
        // فشل جلب الباقات لا يمنع فتح الحوار — نترك planes فارغة
        setPlanes([]);
      }
    };
    fetchPlanes();
  }, [open]);

  useEffect(() => {
    if (open) {
      setError(null);
      setForm((f) => ({ ...f, user_id: userId ?? "" }));
    }
  }, [open, userId]);

  const handleChange = (key) => (e) => {
    const val = e?.target?.value;
    setForm((s) => {
      const updated = { ...s, [key]: val };
      // إذا تم اختيار باقة، نملأ الميزات تلقائياً من features الباقة
      if (key === "plane_id" && val) {
        const selectedPlane = planes.find((p) => String(p.id) === String(val));
        if (selectedPlane && Array.isArray(selectedPlane.features)) {
          // تحويل ميزات الباقة إلى نموذج form.features
          updated.features = selectedPlane.features.map((feat) => ({
            feature_id: String(feat.id ?? ""),
            value: feat.pivot?.value ?? "1",
            yearly_cost: feat.pivot?.yearly_cost ?? "",
            monthly_cost: feat.pivot?.monthly_cost ?? "",
            start_date: "",
            end_date: "",
          }));
        }
      }
      // إذا تم تغيير تاريخ البدء أو الانتهاء الرئيسي، نسخها لكل الميزات
      if (key === "start_date" || key === "end_date") {
        updated.features = (s.features || []).map((feat) => ({
          ...feat,
          [key]: val,
        }));
      }
      return updated;
    });
  };

  const addFeature = () =>
    setForm((s) => ({
      ...s,
      features: [
        ...(s.features || []),
        {
          feature_id: "",
          value: "",
          yearly_cost: "",
          monthly_cost: "",
          start_date: "",
          end_date: "",
        },
      ],
    }));
  const removeFeature = (i) => setForm((s) => ({ ...s, features: s.features.filter((_, idx) => idx !== i) }));
  const changeFeature = (i, key) => (e) => {
    const v = e?.target?.value;
    setForm((s) => {
      const features = [...(s.features || [])];
      features[i] = { ...features[i], [key]: v };
      return { ...s, features };
    });
  };

  const handleSubmit = async () => {
    setError(null);
    setCreating(true);
    try {
      const token = localStorage.getItem("authToken");
      const payload = {
        domain: form.domain || undefined,
        user_id: form.user_id || undefined,
        plane_id: form.plane_id || undefined,
        cost: form.cost || undefined,
        start_date: form.start_date || undefined,
        end_date: form.end_date || undefined,
        is_demo: form.is_demo,
        // تحويل الأرقام وحذف القيم الفارغة
        features: Array.isArray(form.features)
          ? form.features.map((f) => {
              const feat = {
                feature_id: f.feature_id || undefined,
                value: f.value || undefined,
              };
              // تحويل yearly_cost و monthly_cost إلى أرقام صحيحة
              if (f.yearly_cost) {
                const yearly = parseInt(f.yearly_cost, 10);
                if (!isNaN(yearly)) feat.yearly_cost = yearly;
              }
              if (f.monthly_cost) {
                const monthly = parseInt(f.monthly_cost, 10);
                if (!isNaN(monthly)) feat.monthly_cost = monthly;
              }
              if (f.start_date) feat.start_date = f.start_date;
              if (f.end_date) feat.end_date = f.end_date;
              return feat;
            })
          : [],
      };
      console.log("Payload being sent:", payload);
      
      await createVersionToUser(payload, token);
      if (typeof onCreated === "function") onCreated();
      onClose?.();
    } catch (err) {
      console.error("Error creating version:", err);
      // محاولة استخراج رسالة الخطأ من الاستجابة
      const errorMsg = 
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        err?.msg ||
        "خطأ أثناء إنشاء النسخة";
      // إذا كان هناك errors محددة (مثل validation errors)
      if (err?.response?.data?.errors) {
        const errors = err.response.data.errors;
        const errorList = Object.keys(errors).map(k => `${k}: ${errors[k].join(', ')}`).join('\n');
        setError(`${errorMsg}\n${errorList}`);
      } else {
        setError(errorMsg);
      }
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          pb: 2,
          background: `linear-gradient(135deg, ${themeColors.success} 0%, #45A049 100%)`,
          color: "white",
          position: "relative",
        }}
      >
        <Avatar
          sx={{
            width: 48,
            height: 48,
            bgcolor: "rgba(255,255,255,0.25)",
            backdropFilter: "blur(10px)",
          }}
        >
          <AddCircleIcon />
        </Avatar>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            إنشاء نسخة جديدة
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            إنشاء نسخة نظام جديدة للمستخدم
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            left: 8,
            top: 8,
            color: "white",
            "&:hover": {
              bgcolor: "rgba(255,255,255,0.15)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(220, 53, 69, 0.15)",
              whiteSpace: "pre-line",
            }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Basic Information Section */}
        <Card
          elevation={0}
          sx={{
            p: 2.5,
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${themeColors.border.light}`,
            bgcolor: themeColors.background.default,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, mb: 2, color: themeColors.text.primary }}
          >
            📋 المعلومات الأساسية
          </Typography>

          <Box display="flex" flexDirection="column" gap={2.5}>
            <TextField
              label="النطاق (Domain)"
              placeholder="example.restocash.com"
              fullWidth
              value={form.domain}
              onChange={handleChange("domain")}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LanguageIcon sx={{ color: themeColors.text.secondary }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  "&:hover fieldset": {
                    borderColor: themeColors.success,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: themeColors.success,
                  },
                },
              }}
            />

            <TextField
              label="معرّف المستخدم"
              fullWidth
              value={form.user_id}
              disabled
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon sx={{ color: themeColors.text.secondary }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: themeColors.background.hover,
                },
              }}
            />

            <TextField
              select
              label="اختر الباقة"
              fullWidth
              value={String(form.plane_id ?? "")}
              onChange={handleChange("plane_id")}
              helperText={
                planes.length === 0
                  ? "⚠️ لم يتم جلب الباقات"
                  : `✓ متوفر ${planes.length} باقة`
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CategoryIcon sx={{ color: themeColors.text.secondary }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  "&:hover fieldset": {
                    borderColor: themeColors.success,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: themeColors.success,
                  },
                },
              }}
            >
              <MenuItem value={""}>-- اختر باقة --</MenuItem>
              {planes.map((p) => (
                <MenuItem key={p.id} value={String(p.id)}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: themeColors.success,
                      }}
                    />
                    {p.plane_name ?? p.name ?? `#${p.id}`}
                  </Box>
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="التكلفة"
              placeholder="0.00"
              fullWidth
              value={form.cost}
              onChange={handleChange("cost")}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CreditCardIcon sx={{ color: themeColors.text.secondary }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  "&:hover fieldset": {
                    borderColor: themeColors.success,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: themeColors.success,
                  },
                },
              }}
            />

            <Box display="flex" gap={2}>
              <TextField
                label="تاريخ البدء"
                type="date"
                fullWidth
                value={form.start_date}
                onChange={handleChange("start_date")}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarTodayIcon sx={{ color: themeColors.text.secondary }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover fieldset": {
                      borderColor: themeColors.success,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: themeColors.success,
                    },
                  },
                }}
              />

              <TextField
                label="تاريخ الانتهاء"
                type="date"
                fullWidth
                value={form.end_date}
                onChange={handleChange("end_date")}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarTodayIcon sx={{ color: themeColors.text.secondary }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover fieldset": {
                      borderColor: themeColors.success,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: themeColors.success,
                    },
                  },
                }}
              />
            </Box>

            <TextField
              select
              label="نوع النسخة"
              fullWidth
              value={String(form.is_demo ?? "0")}
              onChange={handleChange("is_demo")}
              helperText="اختر: نسخة كاملة أو تجريبية"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  "&:hover fieldset": {
                    borderColor: themeColors.success,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: themeColors.success,
                  },
                },
              }}
            >
              <MenuItem value={"0"}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip label="كاملة" size="small" color="success" />
                  نسخة كاملة
                </Box>
              </MenuItem>
              <MenuItem value={"1"}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip label="تجريبية" size="small" color="warning" />
                  نسخة تجريبية
                </Box>
              </MenuItem>
            </TextField>
          </Box>
        </Card>

        {/* Features Section */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 2,
            border: `1px solid ${themeColors.border.light}`,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              p: 2,
              bgcolor: `${themeColors.primary.main}08`,
              borderBottom: `1px solid ${themeColors.border.light}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
            }}
            onClick={() => setFeaturesExpanded(!featuresExpanded)}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <StarsIcon sx={{ color: themeColors.primary.main }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                الميزات الإضافية
              </Typography>
              <Chip
                label={form.features?.length || 0}
                size="small"
                sx={{
                  bgcolor: themeColors.primary.main,
                  color: "white",
                  fontWeight: 600,
                }}
              />
            </Box>
            <IconButton size="small">
              {featuresExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>

          <Collapse in={featuresExpanded}>
            <Box sx={{ p: 2.5 }}>
              {(form.features || []).map((f, i) => (
                <Card
                  key={i}
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 2,
                    bgcolor: themeColors.background.default,
                    border: `1px solid ${themeColors.border.light}`,
                    borderRadius: 2,
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      ميزة #{i + 1}
                    </Typography>
                    <IconButton
                      color="error"
                      onClick={() => removeFeature(i)}
                      size="small"
                      sx={{
                        "&:hover": {
                          bgcolor: `${themeColors.error}15`,
                        },
                      }}
                    >
                      <RemoveCircleOutlineIcon />
                    </IconButton>
                  </Box>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Box display="flex" gap={2}>
                      <TextField
                        label="معرّف الميزة"
                        value={f.feature_id}
                        onChange={changeFeature(i, "feature_id")}
                        size="small"
                        fullWidth
                      />
                      <TextField
                        label="القيمة"
                        value={f.value}
                        onChange={changeFeature(i, "value")}
                        size="small"
                        fullWidth
                      />
                    </Box>
                    <Box display="flex" gap={2}>
                      <TextField
                        label="السعر السنوي"
                        value={f.yearly_cost}
                        onChange={changeFeature(i, "yearly_cost")}
                        size="small"
                        fullWidth
                      />
                      <TextField
                        label="السعر الشهري"
                        value={f.monthly_cost}
                        onChange={changeFeature(i, "monthly_cost")}
                        size="small"
                        fullWidth
                      />
                    </Box>
                    <Box display="flex" gap={2}>
                      <TextField
                        label="تاريخ البدء"
                        type="date"
                        value={f.start_date}
                        onChange={changeFeature(i, "start_date")}
                        size="small"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        label="تاريخ الانتهاء"
                        type="date"
                        value={f.end_date}
                        onChange={changeFeature(i, "end_date")}
                        size="small"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    </Box>
                  </Box>
                </Card>
              ))}

              <Button
                startIcon={<AddIcon />}
                onClick={addFeature}
                fullWidth
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  borderStyle: "dashed",
                  borderWidth: 2,
                  py: 1.5,
                  borderColor: themeColors.primary.main,
                  color: themeColors.primary.main,
                  "&:hover": {
                    borderColor: themeColors.primary.dark,
                    bgcolor: `${themeColors.primary.main}08`,
                  },
                }}
              >
                إضافة ميزة جديدة
              </Button>
            </Box>
          </Collapse>
        </Card>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2.5, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={creating}
          variant="outlined"
          sx={{
            borderRadius: 2,
            px: 3,
            borderColor: themeColors.border.main,
            color: themeColors.text.secondary,
            "&:hover": {
              borderColor: themeColors.text.primary,
              bgcolor: "transparent",
            },
          }}
        >
          إلغاء
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={creating}
          sx={{
            borderRadius: 2,
            px: 4,
            bgcolor: themeColors.success,
            boxShadow: `0 4px 12px ${themeColors.success}40`,
            "&:hover": {
              bgcolor: "#45A049",
              boxShadow: `0 6px 16px ${themeColors.success}60`,
            },
            "&:disabled": {
              bgcolor: themeColors.text.light,
            },
          }}
        >
          {creating ? (
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                component="span"
                sx={{
                  width: 16,
                  height: 16,
                  border: "2px solid white",
                  borderTopColor: "transparent",
                  borderRadius: "50%",
                  animation: "spin 0.6s linear infinite",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                }}
              />
              <span>جاري الإنشاء...</span>
            </Box>
          ) : (
            "إنشاء النسخة"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
